import { KindeContext } from "@kinde-oss/kinde-auth-react";
import { useBrandAuthConfigOptional, useBrandLogin } from "@nextday/brand-auth";
import { useCallback, useContext } from "react";
import { saveAuthSession } from "@src/auth/AuthCallbackPage";

/**
 * Returns a synchronous click handler for the funnel header's "Log in"
 * control, or `null` when called outside a `BrandAuthProvider` (so the
 * header can hide the button).
 *
 * Persists the current funnel path + brand to sessionStorage so the
 * Kinde callback render can restore both without an application-context
 * refetch.
 */
export const useFunnelLoginHandler = (): (() => void) | null => {
  const brandLogin = useBrandLogin();
  const brandConfig = useBrandAuthConfigOptional();
  const kinde = useContext(KindeContext);

  const onLogin = useCallback((): void => {
    if (!brandLogin || !brandConfig) return;
    saveAuthSession({
      returnPath: window.location.pathname + window.location.search,
      authConfig: brandConfig,
    });
    brandLogin();
  }, [brandLogin, brandConfig]);

  if (!brandLogin || !brandConfig) return null;
  // Hide the Log In control once the user is authenticated. Acts as a
  // visible signal that auth completed and prevents a second redirect
  // round-trip from a logged-in user.
  if (kinde?.isAuthenticated) return null;
  return onLogin;
};
